export type LngLat = [number, number];

export interface RouteRuns {
  /** Tramos únicos de IDA (o el circuito completo, si es CIRCUIT). */
  idaLines: LngLat[][];
  /** Tramos únicos de VUELTA (vacío en CIRCUIT). */
  vueltaLines: LngLat[][];
  /** Tramos donde IDA y VUELTA recorren la misma calle en sentido contrario. */
  sharedLines: LngLat[][];
}

const COORD_ROUND_DECIMALS = 6; // ~0.1m — suficiente para matchear el mismo nodo real

/**
 * Agrupa una secuencia de coordenadas en tramos (polilíneas) contiguos
 * según una clasificación por segmento — en vez de devolver cada segmento
 * de 2 puntos suelto. Esto importa para la densidad de flechas: MapLibre
 * (`symbol-placement: 'line'`) espacia los íconos a lo largo de CADA
 * feature de línea de forma independiente, así que si cada segmento corto
 * fuera su propio feature, cada uno recibiría su propia flecha — el
 * resultado sería más denso, no menos. Agrupando en tramos largos, un
 * giro corto entre dos avenidas largas queda como parte de un solo tramo
 * y no genera una flecha propia.
 */
function groupRuns(coords: LngLat[], isAlt: (segIdx: number) => boolean): { base: LngLat[][]; alt: LngLat[][] } {
  const base: LngLat[][] = [];
  const alt: LngLat[][] = [];
  if (coords.length < 2) return { base, alt };

  let current: LngLat[] = [coords[0]];
  let currentIsAlt = isAlt(0);
  for (let i = 0; i < coords.length - 1; i++) {
    const segIsAlt = isAlt(i);
    if (segIsAlt !== currentIsAlt) {
      (currentIsAlt ? alt : base).push(current);
      current = [coords[i]];
      currentIsAlt = segIsAlt;
    }
    current.push(coords[i + 1]);
  }
  (currentIsAlt ? alt : base).push(current);
  return { base, alt };
}

/**
 * Divide IDA/VUELTA en tramos por categoría (único de cada sentido vs.
 * compartido) para dibujar flechas de dirección con densidad controlada
 * por MapLibre según zoom (`symbol-placement: 'line'`), en vez de puntos
 * calculados a mano cada N metros reales — eso último no se adapta al
 * zoom y en una ruta larga vista completa se ve como una fila continua de
 * triángulos pegados.
 */
export function splitRouteRuns(idaCoords: LngLat[], vueltaCoords: LngLat[], isCircuit: boolean): RouteRuns {
  if (isCircuit) {
    return { idaLines: idaCoords.length >= 2 ? [idaCoords] : [], vueltaLines: [], sharedLines: [] };
  }

  function pointKey([lng, lat]: LngLat): string {
    return `${lng.toFixed(COORD_ROUND_DECIMALS)},${lat.toFixed(COORD_ROUND_DECIMALS)}`;
  }
  function segmentKey(a: LngLat, b: LngLat): string {
    const ka = pointKey(a);
    const kb = pointKey(b);
    return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
  }

  const vueltaSegmentKeys = new Set<string>();
  for (let i = 0; i < vueltaCoords.length - 1; i++) {
    vueltaSegmentKeys.add(segmentKey(vueltaCoords[i], vueltaCoords[i + 1]));
  }
  const idaSegmentKeys = new Set<string>();
  for (let i = 0; i < idaCoords.length - 1; i++) {
    idaSegmentKeys.add(segmentKey(idaCoords[i], idaCoords[i + 1]));
  }
  const isSharedIdaSegment = (i: number): boolean => vueltaSegmentKeys.has(segmentKey(idaCoords[i], idaCoords[i + 1]));
  const isSharedVueltaSegment = (i: number): boolean =>
    idaSegmentKeys.has(segmentKey(vueltaCoords[i], vueltaCoords[i + 1]));

  const idaGrouped = groupRuns(idaCoords, isSharedIdaSegment);
  const vueltaGrouped = groupRuns(vueltaCoords, isSharedVueltaSegment);

  // El tramo compartido se dibuja una sola vez (desde el lado IDA) — desde
  // VUELTA sería la misma calle otra vez, redundante.
  return { idaLines: idaGrouped.base, vueltaLines: vueltaGrouped.base, sharedLines: idaGrouped.alt };
}
